/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import {
  Input,
  Button,
  Form,
  Row,
  Col,
  message,
  Drawer,
  Grid,
} from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  useCreateOperator,
  useGetSingleOperator,
  useUpdateOperator,
} from '../../Pages/operators/Hooks';
import { validations } from '../../utils/validation';
import PropTypes from 'prop-types';
import { countryCode } from '../../utils/utils';
const { useBreakpoint } = Grid;

const NewOperatorModal = ({
  isOpen,
  closeOperatorModel,
  currentOperatorId,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('CreateOperator');
  const [chargerPreview, setChargerPreview] = useState('');
  const [form] = Form.useForm(); // Add this line to create a form instance
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { data: updateOperatorData, isLoading: isLoadingData } =
    useGetSingleOperator({
      id: currentOperatorId,
    });

  const { mutate: createOperator, isPending: isCreating } = useCreateOperator();

  const { mutateAsync: updateOperator, isPending: isUpdating } =
    useUpdateOperator();

  const handleFinish = async (values) => {
    let finalData = {
      name: values.name,
      contactPersonFname: values.contactPersonFname,
      contactPersonLname: values.contactPersonLname,
      contactPersonEmailAddress: values.contactPersonEmailAddress,
      ...(values.address && { address: values.address }),
    };

    // Add phone and country code only if phone number exists
    if (values.contactPersonPhoneNumber) {
      finalData.contactPersonPhoneNumber = values.contactPersonPhoneNumber;
      finalData.countryCode = values.countryCode || countryCode;
    }

    // Only include prefix when creating a new operator
    if (!currentOperatorId) {
      finalData.prefix = values.prefix;
    }

    try {
      if (currentOperatorId) {
        await updateOperator(
          { data: finalData, id: updateOperatorData._id },
          {
            onSuccess: () => {
              message.success(t('operator_updated'));
              queryClient.invalidateQueries(['listOperators']);
              closeOperatorModel(); // CLOSE MODAL ONLY ON SUCCESS
            },
            onError: (error) => {
              message.error(
                error?.response?.data?.message ||
                error?.response?.data?.status_message ||
                t('operator_update_failed')
              );
            },
          }
        );
      } else {
        createOperator(finalData, {
          onSuccess: () => {
            message.success(t('operator_created'));
            queryClient.invalidateQueries(['listOperators']);
            closeOperatorModel();
          },
          onError: (error) => {
            message.error(
              error?.response?.data?.message ||
              error?.response?.data?.status_message
            );
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (updateOperatorData) {
      if (updateOperatorData?.prefix) {
        setChargerPreview(`${updateOperatorData.prefix}1`);
      }
    }
  }, [updateOperatorData]);

  return (
    <Drawer
      maskClosable={false}
      destroyOnHidden
      footer={null}
      open={isOpen}
      title={currentOperatorId ? t('edit_operator') : t('create_operator')}
      width={isMobile ? '100%' : '60%'}
      loading={isLoadingData}
      onClose={closeOperatorModel}
      onCancel={closeOperatorModel}
    >
      <Form
        form={form} // Add this line to bind the form instance
        initialValues={{ ...updateOperatorData, countryCode: '90' }} // Set default countryCode to 90
        layout="vertical"
        className="space-y-4"
        onFinish={handleFinish}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item
              label={t('operator_name')}
              name="name"
              rules={[
                { required: true, message: t('required') },
                { max: 30, message: t('max_characters') },
                validations.noSpaces(t('NoWhitespaceAllowed')), // Updated message
              ]}
            >
              <Input placeholder={t('enter_operator_name')} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={t('prefix')}
              name="prefix"
              rules={[
                {
                  required: !currentOperatorId,
                  message: t('prefix_required'),
                },
                { pattern: /^[A-Z]{1,5}$/, message: t('prefix_invalid') },
              ]}
              tooltip={t('prefixHelper')}
            >
              <Input
                maxLength={5}
                disabled={currentOperatorId}
                suffix={
                  chargerPreview.length > 0 && <div className="flex items-center gap-2 bg-white">
                    {t('preview')}: <strong>{chargerPreview}</strong>
                  </div>
                }
                styles={{
                  suffix: {
                    backgroundColor: 'white',
                  },
                }}
                placeholder={t('enter_prefix')} // Correct placeholder
                value={form.getFieldValue('prefix')} // Add this line to control the input value
                onChange={(e) => {
                  const upperCaseValue = e.target.value.toUpperCase();
                  setChargerPreview(`${upperCaseValue}1`);
                  form.setFieldsValue({ prefix: upperCaseValue }); // Update the form state with the uppercase value
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('contact_person_first_name')}
              name="contactPersonFname"
              rules={[
                { required: true, message: t('required') },
                validations.noSpaces(t('letters_only')),
                validations.nameOnly(t('letters_only')),
              ]}
            >
              <Input placeholder={t('enter_first_name')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('contact_person_last_name')}
              name="contactPersonLname"
              rules={[
                { required: true, message: t('required') },
                validations.noSpaces(t('letters_only')),
                validations.nameOnly(t('letters_only')),
              ]}
            >
              <Input placeholder={t('enter_last_name')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('contact_email')}
              name="contactPersonEmailAddress"
              rules={[
                { required: true, message: t('required') },
                { type: 'email', message: t('invalid_email') },
              ]}
              tooltip={t('emailHelper')}
            >
              <Input placeholder={t('enter_email')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('phone_number')}>
              <Form.Item
                name="contactPersonPhoneNumber"
                noStyle
                rules={[validations.phoneNumber(t('invalid_phone'))]}
              >
                <Input
                  placeholder={t('enter_phone_number')}
                  className="w-full"
                  prefix="+90"
                />
              </Form.Item>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={t('address')}
              name="address"
              rules={[
                validations.noSpaces(t('NoWhitespaceAllowed')), // Updated message
                { required: false, message: t('required') },
              ]}
              tooltip={t('addressHelper')}
            >
              <Input.TextArea rows={2} placeholder={t('enter_address')} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <Button
            htmlType="submit"
            loading={isCreating || isUpdating}
            type="primary"
          >
            {t('SubmitButton')}
          </Button>
          <Button style={{ marginLeft: 10 }} onClick={closeOperatorModel}>
            {t('CancelButton')}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

NewOperatorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeOperatorModel: PropTypes.func.isRequired,
  currentOperatorId: PropTypes.string,
};

export default NewOperatorModal;
